"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { getVoices, CreatorVoiceError, type Voice } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { inputClass, ErrorBox, JobProgress, SharedFreeTrialNote } from "@/components/creatorVoice/CreatorVoiceUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import VoicePickerModal from "@/components/creatorVoice/VoicePickerModal";
import { usePlan } from "@/lib/platform/usePlan";
import Spinner from "@/components/Spinner";

type Short = { hook: string; script: string; audio_url: string; captions_url: string };

function tierLabel(v: Voice): string {
  if (v.tier === "pro") return "🔒 Pro";
  if (v.is_premium) return "★ Premium";
  return "Free";
}

const PersonIcon = (
  <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
    <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.2-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.8-3.6-5-8-5Z" />
  </svg>
);

export default function CreateShortsTool() {
  // create_short_worker only handles the kokoro/piper engines — Chatterbox
  // (Turbo or Pro) voices aren't supported in this batch pipeline, so
  // they're filtered out here rather than letting the job fail server-side.
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [topic, setTopic] = useState("");
  // Generating multiple variants burns more compute per click than a single
  // try — only Pro subscribers get more than one, same as Pro-tier voices.
  const [count, setCount] = useState(1);
  const [voiceId, setVoiceId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const { job, submitting, error, capped, run } = useJob();
  const { isPro } = usePlan();

  useEffect(() => {
    getVoices()
      .then((v) => {
        const supported = v.filter((voice) => !voice.is_premium && voice.tier !== "pro");
        setVoices(supported);
        if (supported[0]) setVoiceId(supported[0].id);
      })
      .catch((e) => setLoadError(e instanceof CreatorVoiceError ? e.message : "Couldn't load voices."))
      .finally(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (!isPro && count > 1) setCount(1);
  }, [isPro, count]);

  const selectedVoice = voices.find((v) => v.id === voiceId) ?? null;
  const shorts = job?.result?.shorts as Short[] | undefined;

  return (
    <div>
      {loadError ? <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">{loadError}</p> : null}

      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">What's your video about?</label>
      <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={3} placeholder="e.g. 5 productivity tips for remote workers" className={inputClass} />

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Number of variants</label>
          <input
            type="number"
            min={1}
            max={isPro ? 10 : 1}
            value={count}
            disabled={!isPro}
            onChange={(e) => setCount(Math.max(1, Math.min(isPro ? 10 : 1, parseInt(e.target.value) || 1)))}
            className={`${inputClass} ${!isPro ? "opacity-60" : ""}`}
          />
          {!isPro ? (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Free plan generates 1 variant —{" "}
              <Link href="/pricing" className="font-medium text-rose-600 hover:underline dark:text-rose-400">
                upgrade to Nohaya Pro
              </Link>{" "}
              for up to 10 at once.
            </p>
          ) : null}
        </div>
        <div className={`relative ${pickerOpen ? "min-h-[420px]" : ""}`}>
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Voice</label>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            disabled={optionsLoading}
            className="mt-2 flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-rose-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-[#0f172a] dark:hover:border-rose-700"
          >
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white">
              {optionsLoading ? <Spinner className="h-4 w-4" /> : PersonIcon}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                {optionsLoading ? "Loading voices…" : selectedVoice?.label ?? "Choose a voice…"}
              </span>
              {selectedVoice && !optionsLoading ? (
                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                  {tierLabel(selectedVoice)} · {selectedVoice.language_label}
                </span>
              ) : null}
            </span>
            <span className="flex-shrink-0 text-slate-400">›</span>
          </button>
          {pickerOpen ? (
            <VoicePickerModal voices={voices} selectedId={voiceId} onSelect={setVoiceId} onClose={() => setPickerOpen(false)} />
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => run("create_short", { topic: topic.trim(), count, voice_id: voiceId })}
          disabled={submitting || optionsLoading || !topic.trim() || !voiceId}
          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Spinner className="h-4 w-4" />
              Submitting…
            </>
          ) : (
            "Generate Shorts"
          )}
        </button>
      </div>
      <SharedFreeTrialNote />

      {error ? <ErrorBox message={error} /> : null}
      {capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      <JobProgress job={job} />

      {job?.status === "completed" && shorts?.length ? (
        <div className="mt-4 space-y-4">
          {shorts.map((s, i) => (
            <div key={i} className="rounded-xl border border-rose-200/80 bg-rose-50/60 p-4 dark:border-rose-800/60 dark:bg-rose-500/5">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Variant {i + 1}</p>
              <p className="mt-2 text-sm font-medium text-slate-900 dark:text-white">{s.hook}</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-400">{s.script}</p>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio controls src={s.audio_url} className="mt-3 w-full" />
              <div className="mt-2 flex gap-4 text-xs">
                <a href={s.audio_url} download className="font-medium text-rose-600 hover:underline dark:text-rose-400">
                  Download audio
                </a>
                <a href={s.captions_url} download className="font-medium text-rose-600 hover:underline dark:text-rose-400">
                  Download captions (.srt)
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
